import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  List,
  Form,
  Icon,
  Segment,
  Confirm,
  Message,
} from "semantic-ui-react";
import { useMutation } from "@apollo/client";
import { RESEND_INVITATION } from "../GraphQL";
import UserContext from "contexts/UserContext";
import LinkButton from "./LinkButton";

const UserListItem = ({ user, onRemove }) => {
  const appUser = useContext(UserContext);
  const [invitationStatus, setInvitationStatus] = useState("");
  const [resendInvitation] = useMutation(RESEND_INVITATION);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const userStatus =
    user.status === "FORCE_CHANGE_PASSWORD" ? "Pending" : "Active";

  return (
    <List.Item>
      <List.Content>
        <Icon name="user" disabled={userStatus === "Pending"} />
        <span title={user.email}>{user.name}</span>{" "}
        <small>
          <i>{userStatus}</i> {invitationStatus}{" "}
          {invitationStatus === "" &&
          userStatus === "Pending" &&
          appUser.canAddUsers ? (
            <LinkButton
              onClick={async () => {
                setInvitationStatus("Resending...");
                await resendInvitation({ variables: { user: user.sub } });
                setInvitationStatus("Invitation sent!");
              }}
            >
              Resend Invitation
            </LinkButton>
          ) : null}{" "}
          <LinkButton type="button" onClick={() => setShowRemoveModal(true)}>
            Remove
          </LinkButton>
        </small>
      </List.Content>
      <Confirm
        open={showRemoveModal}
        content={`Are you sure you want to remove ${user.name}?`}
        size="tiny"
        confirmButton={{
          negative: true,
          loading: isRemoving,
          children: "Remove",
        }}
        onCancel={() => setShowRemoveModal(false)}
        onConfirm={async () => {
          setIsRemoving(true);
          await onRemove();
          setIsRemoving(false);
          setShowRemoveModal(false);
        }}
      />
    </List.Item>
  );
};

const SimpleUserList = ({
  users,
  error,
  onAddUser,
  onRemoveUser,
  addLabel,
  disabled,
}) => {
  const appUser = useContext(UserContext);
  const [isAdding, setIsAdding] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onAddUser(userEmail);
      setIsAdding(false);
      setFormError("");
      setUserEmail("");
    } catch {
      // surfaced via the `error` prop effect below
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (error) {
      setFormError(error);
      setIsSaving(false);
    }
  }, [error]);

  const renderForm = () => {
    if (isAdding) {
      return (
        <Segment>
          <Form.Input
            fluid
            label="Email Address"
            placeholder="msmith@knowmyday.com"
            name="email"
            type="email"
            autoCorrect="off"
            autoCapitalize="none"
            value={userEmail}
            disabled={disabled}
            onKeyPress={(evt) => {
              if (evt.key === "Enter") {
                evt.preventDefault();
                evt.stopPropagation();
                handleSubmit();
              }
            }}
            onChange={(e, { value }) => setUserEmail(value)}
          />
          <Form.Group>
            <Form.Button
              loading={isSaving}
              basic
              primary
              disabled={
                disabled ||
                isSaving ||
                !userEmail ||
                !userEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) ||
                users
                  .map((u) => u.email.toLowerCase())
                  .includes(userEmail.toLowerCase())
              }
              type="button"
              onClick={handleSubmit}
            >
              Invite
            </Form.Button>
            <Form.Button
              basic
              type="button"
              onClick={() => {
                setIsAdding(false);
                setFormError("");
                setUserEmail("");
              }}
            >
              Cancel
            </Form.Button>
          </Form.Group>
          {formError && (
            <Message negative>
              <Message.Content>
                {formError.graphQLErrors.map(({ message }, i) => (
                  <span key={i}>{message}</span>
                ))}
              </Message.Content>
            </Message>
          )}
        </Segment>
      );
    }
    return (
      <Button
        basic
        type="button"
        disabled={disabled}
        onClick={() => setIsAdding(true)}
      >
        <Button.Content>
          <Icon name="user plus" /> {addLabel}
        </Button.Content>
      </Button>
    );
  };

  return (
    <div>
      <List>
        {users.filter(u => u != null).map((u, i) => (
          <UserListItem
            key={u.sub}
            user={u}
            onRemove={() => onRemoveUser(u, i)}
          />
        ))}
      </List>
      {appUser.canAddUsers ? renderForm() : null}
    </div>
  );
};

SimpleUserList.defaultProps = {
  addLabel: "Add User",
  disabled: false,
};

export default SimpleUserList;
